import type { Report } from "@/types/Views/ProjectDashboard/Report";

// How an exported report names the state it came from (RTLCA-5280).
//
// Shared services rather than either caller: the two readers are the dashboard's report selector
// (features/ReportsSelect) and the history page's export rows (pages/ProjectVersionHistory), and one
// wording has to serve both — a file described as "v5" in one place and "version 5" in the other reads
// as two different facts about the same export. Importing it out of the ReportsSelect feature would
// also mean importing that feature's index, which drags a whole component graph into a pure label
// lookup (and into the unit tests of anything that uses it).

/** The version marker, in the same `v<n>` form the history timeline badges use. */
export const reportVersionLabel = (report: Pick<Report, "versionNumber">): string => `v${report.versionNumber}`;

/**
 * The save point's name, or a stand-in when it cannot be read. `revision` comes through a Hasura
 * relationship, so the only way it is null is a report row whose revision has since gone — which the
 * cascade makes impossible in practice, but a list row must still render rather than print
 * "undefined".
 */
export const reportRevisionLabel = (report: Pick<Report, "revision">): string => report.revision?.name ?? "Unknown revision";

/**
 * Both coordinates on one line: which save point, and where in the trail it stood. Reads as
 * `Revision “Concept design” · v12`.
 */
export const reportProvenanceLabel = (report: Pick<Report, "revision" | "versionNumber">): string =>
  `Revision “${reportRevisionLabel(report)}” · ${reportVersionLabel(report)}`;

/**
 * True while the project is still on the revision this report was exported from — `frozenAt` is set
 * the instant a save point stops being current. Not the same as "up to date": changes since the
 * export still move the version number, which is why the number is shown next to it rather than
 * instead of it.
 */
export const isReportFromCurrentRevision = (report: Pick<Report, "revision">): boolean =>
  report.revision !== null && report.revision.frozenAt === null;
